/**
 * New / edit estimate (tracker item 39). A new estimate picks the customer and
 * one of their properties (it cannot be moved later); an existing one can be
 * edited only while it is a DRAFT. Lines are catalog items with a quantity, an
 * optional manual price (blank = the item's catalog or price-chart price at
 * the property) and an optional customer-facing description. Totals, tax and
 * discounts always come from the server's preview (the same pricing a save
 * stores); the discounts that apply are ticked by default and can be removed.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createAPIClient, IBundlesApi, IEstimatesApi, IItemsApi, IPropertiesApi } from '../../api';
import * as types from '../../api/types';
import { CustomerPicker } from '../../components/CustomerPicker';
import { inputClass, labelClass, primaryButtonClass, rowDangerActionClass, secondaryButtonClass } from '../../components/formStyles';
import { getErrorMessage } from '../../utils/apiError';
import { invoiceMoney } from '../invoiceUi';
import {
  DraftBundle, ESTIMATES_PATH, EstimateLineDraft, bundleDayLabel, bundleGroupTotals, draftSections, draftsToInput, estimateAddress, estimateErrorText,
  estimatePath, lineDraftProblem, lineToDraft, localDatePlus, newLineDraft,
} from './estimateUi';
import { formatJobDate } from '../../utils/jobPresentation';

type Api = IEstimatesApi & IItemsApi & IPropertiesApi & Pick<IBundlesApi, 'listServiceBundles'>;

const ITEM_PAGES = 5;

export function EstimateFormPage() {
  const { id } = useParams<{ id: string }>();
  const editing = !!id;
  const api = useMemo<Api>(() => createAPIClient(), []);
  const navigate = useNavigate();

  const [existing, setExisting] = useState<types.Estimate | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState('');
  const [properties, setProperties] = useState<types.Property[]>([]);
  const [propertyId, setPropertyId] = useState('');
  const [items, setItems] = useState<types.Item[]>([]);
  const [title, setTitle] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [expiryDate, setExpiryDate] = useState(localDatePlus(30));
  const [lines, setLines] = useState<EstimateLineDraft[]>([newLineDraft()]);
  /** null = every applicable discount (the server's default) */
  const [discountIds, setDiscountIds] = useState<string[] | null>(null);
  const [preview, setPreview] = useState<types.EstimatePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previewId = useRef(0);

  // Service bundles (tracker item 85): the picker, the panel's fields and the per-bundle view state
  const [bundleOptions, setBundleOptions] = useState<types.ServiceBundle[]>([]);
  const [bundlePick, setBundlePick] = useState('');
  const [bundleStart, setBundleStart] = useState('');
  const [bundleBusy, setBundleBusy] = useState(false);
  const [bundleError, setBundleError] = useState<string | null>(null);
  const [bundleNotice, setBundleNotice] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [dateEdits, setDateEdits] = useState<Record<string, string>>({});
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  // The catalog (a few pages of 100)
  useEffect(() => {
    let active = true;
    (async () => {
      const all: types.Item[] = [];
      for (let page = 1; page <= ITEM_PAGES; page += 1) {
        const result = await api.listItems(page, 100);
        all.push(...result.data);
        if (page >= result.pagination.pages) break;
      }
      if (active) setItems(all);
    })().catch((cause) => { if (active) setError(getErrorMessage(cause, 'Could not load the catalog items')); });
    return () => { active = false; };
  }, [api]);

  // Editing: load the estimate into the form
  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    api.getEstimate(id).then((estimate) => {
      if (!active) return;
      setExisting(estimate);
      setCustomerId(estimate.customer.id);
      setPropertyId(estimate.property.id);
      setTitle(estimate.title ?? '');
      setCustomerMessage(estimate.customerMessage ?? '');
      setInternalNotes(estimate.internalNotes ?? '');
      setExpiryDate(estimate.expiryDate);
      setLines(estimate.lineItems.length ? estimate.lineItems.map(lineToDraft) : [newLineDraft()]);
      setDiscountIds(estimate.discounts.map((d) => d.discountId).filter((x): x is string => !!x));
    }).catch((cause) => { if (active) setLoadError(getErrorMessage(cause, 'Could not load the estimate')); });
    return () => { active = false; };
  }, [api, id]);

  // The bundles that can be added (active ones; a failure only hides the picker)
  const editableDraft = !!existing && existing.status === 'DRAFT';
  useEffect(() => {
    if (!editableDraft) return undefined;
    let active = true;
    api.listServiceBundles({ isActive: true })
      .then((list) => { if (active) setBundleOptions(list); })
      .catch(() => { if (active) setBundleOptions([]); });
    return () => { active = false; };
  }, [api, editableDraft]);

  // A new estimate: the chosen customer's properties
  useEffect(() => {
    if (editing) return undefined;
    setProperties([]);
    setPropertyId('');
    if (!customerId) return undefined;
    let active = true;
    api.listProperties(customerId, 1, 100)
      .then((result) => {
        if (!active) return;
        setProperties(result.data);
        if (result.data.length === 1) setPropertyId(result.data[0].id);
      })
      .catch((cause) => { if (active) setError(getErrorMessage(cause, "Could not load the customer's properties")); });
    return () => { active = false; };
  }, [api, customerId, editing]);

  const linesProblem = lineDraftProblem(lines);
  const linesKey = JSON.stringify(draftsToInput(lines));

  // Totals from the server (debounced), whenever the lines, the property or the discounts change
  const refreshPreview = useCallback(async () => {
    const requestId = ++previewId.current;
    if (!propertyId || linesProblem) { setPreview(null); setPreviewError(null); return; }
    try {
      const result = await api.previewEstimate({ propertyId, lineItems: JSON.parse(linesKey), discountIds });
      if (requestId === previewId.current) { setPreview(result); setPreviewError(null); }
    } catch (cause) {
      if (requestId === previewId.current) { setPreview(null); setPreviewError(estimateErrorText(cause, 'Could not price these lines')); }
    }
  }, [api, propertyId, linesProblem, linesKey, discountIds]);

  useEffect(() => {
    const timer = setTimeout(() => { void refreshPreview(); }, 300);
    return () => clearTimeout(timer);
  }, [refreshPreview]);

  const updateLine = (key: string, patch: Partial<EstimateLineDraft>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));

  const toggleDiscount = (discountId: string, on: boolean) => {
    const current = discountIds ?? (preview?.applicableDiscounts.filter((d) => d.selected).map((d) => d.discountId) ?? []);
    setDiscountIds(on ? [...new Set([...current, discountId])] : current.filter((x) => x !== discountId));
  };

  /** The server edited the stored estimate (bundle added, dated or removed): keep it for the group totals, keep the unsaved line edits. */
  async function bundleAction(work: () => Promise<void>, fallback: string) {
    setBundleBusy(true);
    setBundleError(null);
    setBundleNotice(null);
    try {
      await work();
    } catch (cause) {
      setBundleError(estimateErrorText(cause, fallback));
    } finally {
      setBundleBusy(false);
    }
  }

  const addBundle = () => bundleAction(async () => {
    if (!id || !bundlePick) return;
    const result = await api.addEstimateBundle(id, { bundleId: bundlePick, ...(bundleStart ? { startDate: bundleStart } : {}) });
    const fresh = result.lineItems.filter((line) => line.bundleId === bundlePick).map(lineToDraft);
    // A blank placeholder line (an estimate with no lines yet) would only block the save
    setLines((current) => [...current.filter((line) => line.itemId || line.lineId || line.bundle), ...fresh]);
    setExisting(result);
    setBundlePick('');
    setBundleStart('');
    const skipped = result.skipped.length > 0
      ? ` ${result.skipped.length} visit${result.skipped.length === 1 ? ' was' : 's were'} left out because the item is no longer available: ${result.skipped.map((x) => x.itemName).join(', ')}.`
      : '';
    setBundleNotice(`Added ${fresh.length} visit${fresh.length === 1 ? '' : 's'} from the bundle.${skipped}`);
  }, 'Could not add the bundle');

  const saveBundleDate = (bundle: DraftBundle, key: string, startDate: string | null) => bundleAction(async () => {
    if (!id || !bundle.bundleId) return;
    const updated = await api.updateEstimateBundle(id, bundle.bundleId, { startDate });
    setExisting(updated);
    setLines((current) => current.map((line) => (line.bundle && line.bundle.bundleId === bundle.bundleId ? { ...line, bundle: { ...line.bundle, startDate } } : line)));
    setDateEdits((current) => { const next = { ...current }; delete next[key]; return next; });
    setBundleNotice(startDate ? `${bundle.bundleName} starts ${formatJobDate(startDate)}.` : `The start date of ${bundle.bundleName} was cleared; it is asked for when the estimate is converted.`);
  }, 'Could not save the start date');

  const removeBundle = (bundle: DraftBundle) => bundleAction(async () => {
    if (!id || !bundle.bundleId) return;
    const updated = await api.removeEstimateBundle(id, bundle.bundleId);
    setExisting(updated);
    setLines((current) => {
      const next = current.filter((line) => line.bundle?.bundleId !== bundle.bundleId);
      return next.length ? next : [newLineDraft()];
    });
    setConfirmRemove(null);
    setBundleNotice(`${bundle.bundleName} was removed from the estimate.`);
  }, 'Could not remove the bundle');

  async function save() {
    setError(null);
    if (!propertyId) { setError('Choose the customer and the property'); return; }
    if (linesProblem) { setError(linesProblem); return; }
    setSaving(true);
    try {
      const common = {
        title: title.trim() || null,
        customerMessage: customerMessage.trim() || null,
        internalNotes: internalNotes.trim() || null,
        expiryDate,
        lineItems: draftsToInput(lines),
        ...(discountIds !== null ? { discountIds } : {}),
      };
      const saved = editing && id
        ? await api.updateEstimate(id, common)
        : await api.createEstimate({ propertyId, ...common });
      navigate(estimatePath(saved.id));
    } catch (cause) {
      setError(estimateErrorText(cause, 'Could not save the estimate'));
    } finally {
      setSaving(false);
    }
  }

  const renderLine = (line: EstimateLineDraft, index: number) => {
    const priced = preview?.lineItems[index];
    return (
      <li key={line.key} className="rounded border border-gray-200 p-3" data-testid="estimate-line">
        {line.bundle && <p className="mb-2 text-xs font-semibold text-blue-800" data-testid="estimate-line-day">{bundleDayLabel(line.bundle.offsetDays)}</p>}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_6rem_8rem]">
          <div>
            <label htmlFor={`${line.key}-item`} className={labelClass}>Item</label>
            <select id={`${line.key}-item`} className={inputClass} value={line.itemId} onChange={(e) => updateLine(line.key, { itemId: e.target.value })}>
              <option value="">Choose an item</option>
              {items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor={`${line.key}-qty`} className={labelClass}>Quantity</label>
            <input id={`${line.key}-qty`} className={inputClass} inputMode="decimal" value={line.quantity} onChange={(e) => updateLine(line.key, { quantity: e.target.value })} />
          </div>
          <div>
            <label htmlFor={`${line.key}-price`} className={labelClass}>Price</label>
            <input id={`${line.key}-price`} className={inputClass} inputMode="decimal" placeholder="Catalog" value={line.unitPrice} onChange={(e) => updateLine(line.key, { unitPrice: e.target.value })} />
          </div>
          <div className="sm:col-span-3">
            <label htmlFor={`${line.key}-description`} className={labelClass}>Description (optional)</label>
            <input id={`${line.key}-description`} className={inputClass} maxLength={500} value={line.description} onChange={(e) => updateLine(line.key, { description: e.target.value })} />
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-gray-600" data-testid="estimate-line-total">
            {priced ? `${invoiceMoney(priced.unitPrice)} each · ${invoiceMoney(priced.lineTotal)} with tax${priced.priceSource === 'CHART' ? ' (price chart)' : ''}` : ' '}
          </span>
          <button type="button" className={rowDangerActionClass} disabled={lines.length === 1} onClick={() => setLines((current) => current.filter((l) => l.key !== line.key))}>
            {line.bundle ? 'Remove visit' : 'Remove line'}
          </button>
        </div>
      </li>
    );
  };

  if (editing && loadError) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Link to={ESTIMATES_PATH} className="text-sm text-blue-700 hover:underline">← Estimates</Link>
        <p role="alert" className="mt-4 rounded bg-red-50 p-3 text-red-800">{loadError}</p>
      </main>
    );
  }
  if (editing && !existing) return <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6"><p role="status">Loading estimate…</p></main>;
  if (existing && existing.status !== 'DRAFT') {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Link to={estimatePath(existing.id)} className="text-sm text-blue-700 hover:underline">← {existing.estimateNumber}</Link>
        <p role="alert" className="mt-4 rounded bg-amber-50 p-3 text-amber-900">Only a draft estimate can be edited.</p>
      </main>
    );
  }

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <Link to={existing ? estimatePath(existing.id) : ESTIMATES_PATH} className="text-sm text-blue-700 hover:underline">
        ← {existing ? existing.estimateNumber : 'Estimates'}
      </Link>
      <h1 className="mt-2 text-2xl font-bold text-gray-900">{existing ? `Edit estimate ${existing.estimateNumber}` : 'New estimate'}</h1>

      <section aria-labelledby="estimate-for" className="mt-4 rounded-lg bg-white p-4 shadow sm:p-5">
        <h2 id="estimate-for" className="mb-3 text-lg font-semibold">For</h2>
        {existing ? (
          <p className="text-sm"><span className="font-semibold">{existing.customer.name}</span> · {estimateAddress(existing.property)}</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CustomerPicker id="estimate-customer" label="Customer" value={customerId} onChange={(value) => setCustomerId(value)} />
            <div>
              <label htmlFor="estimate-property" className={labelClass}>Property</label>
              <select id="estimate-property" className={inputClass} value={propertyId} disabled={!customerId} onChange={(e) => setPropertyId(e.target.value)}>
                <option value="">{customerId ? 'Choose a property' : 'Choose a customer first'}</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{estimateAddress(p)}</option>)}
              </select>
            </div>
          </div>
        )}
      </section>

      <section aria-labelledby="estimate-details" className="mt-4 rounded-lg bg-white p-4 shadow sm:p-5">
        <h2 id="estimate-details" className="mb-3 text-lg font-semibold">Details</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="estimate-title" className={labelClass}>Title</label>
            <input id="estimate-title" className={inputClass} maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Spring clean-up" />
          </div>
          <div>
            <label htmlFor="estimate-expiry" className={labelClass}>Valid until</label>
            <input id="estimate-expiry" type="date" className={inputClass} min={localDatePlus(0)} value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="estimate-message" className={labelClass}>Message to the customer</label>
            <textarea id="estimate-message" className={inputClass} rows={3} maxLength={4000} value={customerMessage} onChange={(e) => setCustomerMessage(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="estimate-notes" className={labelClass}>Internal notes (never shown to the customer)</label>
            <textarea id="estimate-notes" className={inputClass} rows={2} maxLength={4000} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} />
          </div>
        </div>
      </section>

      <section aria-labelledby="estimate-lines" className="mt-4 rounded-lg bg-white p-4 shadow sm:p-5">
        <h2 id="estimate-lines" className="mb-3 text-lg font-semibold">Lines</h2>
        {draftSections(lines).map((section, sectionIndex) => {
          if (!section.bundle) {
            return <ul key={section.key} className="space-y-4">{section.indexes.map((index) => renderLine(lines[index], index))}</ul>;
          }
          const bundle = section.bundle;
          const group = (existing?.bundles ?? []).find((g) => (g.bundleId ?? `name:${g.bundleName}`) === section.key);
          const totals = bundleGroupTotals(section.indexes, preview, group);
          const isCollapsed = !!collapsed[section.key];
          const dateValue = dateEdits[section.key] ?? bundle.startDate ?? '';
          const live = !!bundle.bundleId;
          return (
            <div key={section.key} className="mt-4 rounded border border-blue-200" role="group" aria-label={`Bundle ${bundle.bundleName}`} data-testid="estimate-bundle">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-t bg-blue-50 px-3 py-2">
                <button
                  type="button" className="min-h-[44px] text-left font-semibold text-blue-900" aria-expanded={!isCollapsed}
                  aria-controls={`bundle-body-${sectionIndex}`} onClick={() => setCollapsed((current) => ({ ...current, [section.key]: !current[section.key] }))}
                >
                  <span aria-hidden="true">{isCollapsed ? '▸ ' : '▾ '}</span>{bundle.bundleName}
                  <span className="ml-2 text-sm font-normal text-blue-800">{section.indexes.length} visit{section.indexes.length === 1 ? '' : 's'}</span>
                </button>
                <span className="text-sm text-blue-900" data-testid="estimate-bundle-start">
                  {bundle.startDate ? `Starts ${formatJobDate(bundle.startDate)}` : 'Start date asked when converted'}
                </span>
                {totals && (
                  <span className="text-sm text-blue-900" data-testid="estimate-bundle-totals">
                    Subtotal {invoiceMoney(totals.subtotal)} · Tax {invoiceMoney(totals.tax)} · <strong>Total {invoiceMoney(totals.total)}</strong>
                  </span>
                )}
              </div>
              {!isCollapsed && (
                <div id={`bundle-body-${sectionIndex}`} className="p-3">
                  {live && existing ? (
                    <div className="mb-3 flex flex-wrap items-end gap-2">
                      <div>
                        <label htmlFor={`bundle-start-${sectionIndex}`} className={labelClass}>Start date</label>
                        <input
                          id={`bundle-start-${sectionIndex}`} type="date" className={inputClass} value={dateValue}
                          onChange={(e) => setDateEdits((current) => ({ ...current, [section.key]: e.target.value }))}
                        />
                      </div>
                      <button
                        type="button" className={secondaryButtonClass} disabled={bundleBusy || !dateValue || dateValue === (bundle.startDate ?? '')}
                        onClick={() => void saveBundleDate(bundle, section.key, dateValue)}
                      >
                        Set start date
                      </button>
                      <button type="button" className={secondaryButtonClass} disabled={bundleBusy || !bundle.startDate} onClick={() => void saveBundleDate(bundle, section.key, null)}>
                        Clear date
                      </button>
                      {confirmRemove === section.key ? (
                        <span className="flex flex-wrap items-center gap-2 text-sm" role="alert">
                          Remove {section.indexes.length} visit{section.indexes.length === 1 ? '' : 's'}?
                          <button type="button" className={rowDangerActionClass} disabled={bundleBusy} onClick={() => void removeBundle(bundle)}>Yes, remove bundle</button>
                          <button type="button" className={secondaryButtonClass} onClick={() => setConfirmRemove(null)}>Keep it</button>
                        </span>
                      ) : (
                        <button type="button" className={rowDangerActionClass} disabled={bundleBusy} onClick={() => setConfirmRemove(section.key)}>Remove bundle</button>
                      )}
                    </div>
                  ) : (
                    <p className="mb-3 text-sm text-gray-600">
                      {live ? 'Save the estimate to change this bundle.' : 'This bundle no longer exists. Its lines stay as they are; remove a line to drop that visit.'}
                    </p>
                  )}
                  <ul className="space-y-4">{section.indexes.map((index) => renderLine(lines[index], index))}</ul>
                </div>
              )}
            </div>
          );
        })}
        <button type="button" className={`${secondaryButtonClass} mt-3`} onClick={() => setLines((current) => [...current, newLineDraft()])}>Add line</button>

        <div className="mt-5 border-t pt-4" aria-label="Add a service bundle">
          <h3 className="text-sm font-semibold text-gray-700">Add a service bundle</h3>
          {!existing ? (
            <p className="mt-1 text-sm text-gray-600">Save the draft first, then edit it to add a bundle.</p>
          ) : bundleOptions.length === 0 ? (
            <p className="mt-1 text-sm text-gray-600">There are no active service bundles to add. Create one in Settings &gt; Service bundles.</p>
          ) : (
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
              <div>
                <label htmlFor="estimate-bundle-pick" className={labelClass}>Bundle</label>
                <select id="estimate-bundle-pick" className={inputClass} value={bundlePick} onChange={(e) => setBundlePick(e.target.value)}>
                  <option value="">Choose a bundle</option>
                  {bundleOptions.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.componentCount} visit{b.componentCount === 1 ? '' : 's'})</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="estimate-bundle-start" className={labelClass}>Start date (optional)</label>
                <input id="estimate-bundle-start" type="date" className={inputClass} value={bundleStart} onChange={(e) => setBundleStart(e.target.value)} />
              </div>
              <button type="button" className={primaryButtonClass} disabled={bundleBusy || !bundlePick} onClick={() => void addBundle()}>
                {bundleBusy ? 'Working…' : 'Add bundle'}
              </button>
            </div>
          )}
          {bundleError && <p role="alert" className="mt-2 rounded bg-red-50 p-2 text-sm text-red-800">{bundleError}</p>}
          {bundleNotice && <p role="status" className="mt-2 rounded bg-green-50 p-2 text-sm text-green-800">{bundleNotice}</p>}
        </div>
      </section>

      <section aria-labelledby="estimate-totals" className="mt-4 rounded-lg bg-white p-4 shadow sm:p-5">
        <h2 id="estimate-totals" className="mb-3 text-lg font-semibold">Totals</h2>
        {previewError && <p role="alert" className="mb-2 rounded bg-red-50 p-2 text-sm text-red-800">{previewError}</p>}
        {!preview && !previewError && <p className="text-sm text-gray-600">{propertyId ? (linesProblem ?? 'Pricing…') : 'Choose the property to see the prices.'}</p>}
        {preview && (
          <>
            {preview.applicableDiscounts.length > 0 && (
              <fieldset className="mb-3">
                <legend className="text-sm font-semibold text-gray-700">Discounts</legend>
                {preview.applicableDiscounts.map((d) => (
                  <label key={d.discountId} className="mt-1 flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={d.selected} onChange={(e) => toggleDiscount(d.discountId, e.target.checked)} />
                    {d.name} ({d.type === 'PERCENTAGE' ? `${Number(d.value)}%` : invoiceMoney(d.value)})
                  </label>
                ))}
              </fieldset>
            )}
            <dl className="ml-auto max-w-xs space-y-1 text-sm" aria-label="Estimate totals">
              <div className="flex justify-between gap-6"><dt>Subtotal</dt><dd>{invoiceMoney(preview.subtotal)}</dd></div>
              {Number(preview.totalDiscount) > 0 && <div className="flex justify-between gap-6"><dt>Discounts</dt><dd>−{invoiceMoney(preview.totalDiscount)}</dd></div>}
              <div className="flex justify-between gap-6"><dt>Tax</dt><dd>{invoiceMoney(preview.totalTax)}</dd></div>
              <div className="flex justify-between gap-6 border-t pt-1 font-bold"><dt>Total</dt><dd data-testid="estimate-preview-total">{invoiceMoney(preview.total)}</dd></div>
            </dl>
          </>
        )}
      </section>

      {error && <p role="alert" className="mt-4 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" className={primaryButtonClass} disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : existing ? 'Save changes' : 'Save draft'}
        </button>
        <Link to={existing ? estimatePath(existing.id) : ESTIMATES_PATH} className={secondaryButtonClass}>Cancel</Link>
      </div>
    </main>
  );
}
